import {
    ChangeEventHandler,
    ReactNode,
    HTMLInputTypeAttribute,
    forwardRef,
    Ref,
} from 'react';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { cn } from '../../../utils';
import { AcceptsSetFormItemError } from '../FormItem/FormItem';
import { Prettify } from '@types';

export type FullInputProps = Prettify<
    InputProps & EitherAddonOrIcon & AcceptsSetFormItemError
>;

type InputProps = {
    name: string;
    className?: string | object;
    type?: HTMLInputTypeAttribute;
    onChange?: ChangeEventHandler<HTMLInputElement>;
    disabled?: boolean;
    value?: string;
    defaultValue?: string;
    placeholder?: string;
    error?: string;
    trailingIcon?: ReactNode;
    trailingAddon?: ReactNode;
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
    onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
    wrapperClassname?: string;
};

type EitherAddonOrIcon =
    | {
          leadingAddon?: string;
          leadingIcon?: never;
      }
    | {
          leadingAddon?: never;
          leadingIcon?: ReactNode;
      };

export default forwardRef(function Input(
    {
        type = 'text',
        name,
        className,
        placeholder,
        value,
        defaultValue,
        disabled = false,
        error,
        leadingAddon,
        leadingIcon,
        trailingIcon = false,
        trailingAddon,
        wrapperClassname = '',
        onChange,
        ...rest
    }: FullInputProps,
    ref: Ref<HTMLInputElement>,
) {
    return (
        <div
            data-testid="input-wrapper"
            className={cn(
                'alli-relative alli-mt-2 alli-rounded-md',
                {
                    'alli-shadow-sm': !error && !disabled,
                    'alli-flex': leadingAddon || trailingAddon,
                },
                wrapperClassname,
            )}>
            {leadingAddon && (
                <span className="alli-inline-flex alli-items-center alli-rounded-l-md alli-border alli-border-r-0 alli-border-gray-400 alli-bg-gray-100 alli-px-3 alli-text-blue-gray-700 sm:alli-text-sm">
                    {leadingAddon}
                </span>
            )}
            {leadingIcon && (
                <div className="alli-pointer-events-none alli-absolute alli-inset-y-0 alli-left-0 alli-flex alli-items-center alli-pl-3">
                    {leadingIcon}
                </div>
            )}
            <input
                data-testid={`input-${name}`}
                ref={ref}
                type={type}
                name={name}
                id={name}
                {...rest}
                className={cn(
                    'alli-block alli-w-full alli-border-0 alli-py-1.5 alli-text-blue-gray-700 alli-ring-1 alli-ring-inset alli-ring-gray-400 focus:alli-ring-1 focus:alli-ring-inset sm:alli-text-sm sm:alli-leading-6',
                    {
                        'alli-min-w-0 alli-flex-1 alli-rounded-none alli-rounded-r-md placeholder:alli-text-gray-600':
                            leadingAddon,
                        'alli-rounded-md alli-pr-10 alli-placeholder-gray-600':
                            !leadingAddon,
                        'alli-text-blue-gray-700 alli-ring-red-400 placeholder:alli-text-gray-600 focus:alli-ring-red-400':
                            error,
                        'alli-pl-10': leadingIcon,
                        'alli-cursor-not-allowed alli-border-gray-400 alli-bg-gray-300 alli-text-gray-600':
                            disabled,
                        'focus:alli-shadow-[0_0_4px_rgba(12,105,234,0.30)]':
                            !error,
                        'alli-min-w-0 alli-flex-1 alli-rounded-none alli-rounded-l-md placeholder:alli-text-gray-600':
                            trailingAddon,
                    },
                    className,
                )}
                placeholder={placeholder}
                value={value}
                defaultValue={defaultValue}
                disabled={disabled}
                onChange={onChange}
                readOnly={!onChange}
            />
            {trailingAddon && (
                <span className="alli-inline-flex alli-items-center alli-rounded-r-md alli-border alli-border-l-0 alli-border-gray-400 alli-bg-gray-100 alli-px-3 alli-text-blue-gray-700 sm:alli-text-sm">
                    {trailingAddon}
                </span>
            )}
            {(error || trailingIcon) && (
                <div
                    className={cn(
                        'alli-absolute alli-inset-y-0 alli-right-0 alli-flex alli-items-center alli-pr-3 ',
                        {
                            'alli-text-gray-700': trailingIcon,
                            'alli-pointer-events-none alli-text-red-400': error,
                            'alli-right-12': trailingAddon,
                        },
                    )}>
                    {trailingIcon}
                    {error && <ErrorIcon />}
                </div>
            )}
        </div>
    );
});

function ErrorIcon() {
    return (
        <ExclamationCircleIcon
            className="alli-h-5 alli-w-5"
            aria-hidden="true"
        />
    );
}
